import React, { useState, useEffect } from 'react';
import { Stack, Box, Divider } from '@mui/material';
import sumBy from 'lodash/sumBy';
import Iconify from '@/components//Iconify';
import TicketInfo from './TicketInfo';
import { isEmptyHtml } from '@/utils/validations';
import useMobile from '@/hooks/useMobile';
import { useTicketsSelected, usePromotions } from '@/store';
import CollapseBoxHtml from '@/templates/plus/components/CollapseBoxHtml';
import useAnalyticsEvents from '@/hooks/useAnalyticsEvents';

function TicketItem({
  children,
  ticket,
  onClick = () => {},
  showPrice = false,
  showDesc = false,
  disabled = false,
}) {
  const [show, setShow] = useState(false);
  const { category_color, description, ticket_type_id } = ticket;
  const { isMobile } = useMobile();
  const { ticketsSelected } = useTicketsSelected();
  const { showTicketTypeDetails } = useAnalyticsEvents();
  const [promotion, setPromotion] = useState();
  const { promotions } = usePromotions();

  const quantity = ticketsSelected
    ? sumBy(
        ticketsSelected.filter(
          (t) => t.ticket_type_id === ticket.ticket_type_id,
        ),
        (o) => o.quantity,
      )
    : 0;

  const onCollapseBoxHtmlChangeHandler = (show) => {
    if (show) {
      showTicketTypeDetails(ticket);
    }
  };

  const onClickHandler = () => {
    if (!disabled) {
      onClick();
    }
  };

  useEffect(() => {
    if (promotions) {
      if (
        promotions.valid_for_ticket_type === 'all' ||
        promotions.valid_for_ticket_type === ticket_type_id.toString() ||
        promotions?.valid_for_ticket_type_list?.includes(ticket_type_id)
      ) {
        if (promotions.discount_type === 'bundle') {
          setPromotion(promotions.bundle_rule);
        }
      }
    } else {
      setPromotion(null);
    }
  }, [promotions]);

  useEffect(() => {
    setShow(true);
  }, []);

  return (
    <Box
      sx={{
        opacity: disabled ? 0.5 : 1,
        boxShadow: (theme) => theme.customShadows.card,
        mt: isMobile ? 1 : 2,
        mb: isMobile ? 1 : 2,
        border: (theme) =>
          `0.5px solid ${promotion ? '#139e65' : theme.palette.grey[200]}`,
        borderRadius: '16px',
        ...(!showDesc &&
          !disabled && {
            '&:hover': {
              cursor: 'pointer',
              border: (theme) =>
                `0.5px solid ${theme.palette.primary.palette[200]}`,
              background: (theme) => theme.palette.primary.palette[50],
              boxShadow:
                '0px 0px 2px rgba(145, 158, 171, 0.2), 0px 0px 24px -4px rgba(145, 158, 171, 0.1)',
            },
          }),
        ...(quantity > 0 &&
          !showDesc && {
            border: (theme) =>
              `0.5px solid ${theme.palette.primary.palette[200]}`,
            background: (theme) => theme.palette.primary.palette[50],
            boxShadow:
              '0px 0px 2px rgba(145, 158, 171, 0.2), 0px 0px 24px -4px rgba(145, 158, 171, 0.1)',
          }),
      }}
      onClick={onClickHandler}
    >
      <Stack
        direction="row"
        gap={1}
        sx={{
          width: '100%',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={{ ml: '0.8rem' }}
          spacing={1}
        >
          {showDesc && (
            <Box
              sx={{
                width: '20px',
                height: '20px',
                color: category_color
                  ? category_color
                  : (theme) =>
                      theme.palette.mode === 'light'
                        ? theme.palette.primary.main
                        : theme.palette.secondary.main,
                ml: isMobile ? 1 : 2,
              }}
            >
              <Iconify
                width={20}
                height={20}
                icon={'mdi:ticket-confirmation-outline'}
              />
            </Box>
          )}
          {show && quantity <= 0 && !showDesc && (
            <Box
              sx={{
                width: '20px',
                height: '20px',
                color: category_color
                  ? category_color
                  : (theme) =>
                      theme.palette.mode === 'light'
                        ? theme.palette.primary.main
                        : theme.palette.secondary.main,
                ml: isMobile ? 1 : 2,
              }}
            >
              <Iconify
                width={20}
                height={20}
                icon={'mdi:ticket-confirmation-outline'}
              />
            </Box>
          )}
          {quantity > 0 && !showDesc && (
            <Box
              sx={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: (theme) => theme.palette.primary.main,
                ml: isMobile ? 1 : 2,
                color: (theme) => theme.palette.common.white,
                fontSize: '12px',
                fontWeight: 600,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <>{quantity}</>
            </Box>
          )}

          <Box sx={{ ml: 2, pb: 1.5, pt: 1.5 }}>
            <TicketInfo ticket={ticket} showPrice={showPrice} />
          </Box>
        </Stack>
        <Box sx={{ flexGrow: 1 }}></Box>
        {children}
      </Stack>
      {showDesc && !isEmptyHtml(description) && (
        <Box
          sx={{
            marginLeft: isMobile ? '38px' : '44px',
            marginRight: '12px',
            mb: isMobile ? 1 : 2,
            fontSize: '14px',
          }}
        >
          <Divider sx={{ mb: 1 }} />
          <Box
            sx={{
              ml: isMobile ? 1 : 2,
            }}
          >
            <CollapseBoxHtml
              html={description}
              variant="show-more"
              onChange={onCollapseBoxHtmlChangeHandler}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default TicketItem;
